import api from '../../services/api';

/**
 * Administrator registry of confirmed operational exceptions (including the
 * consistency warnings of decision 2026-09-18) and the purchase-price
 * deviation threshold. Backend: backend/src/routes/operationalExceptions.js.
 */

export type OperationalExceptionScopeType = 'PZ' | 'WZ' | 'TRANSPORT' | 'SUPPLIER_ORDER' | 'ORDER';
export type OperationalExceptionStatusFilter = 'open' | 'resolved' | 'all';

export interface OperationalExceptionDto {
  id: string;
  scopeType: OperationalExceptionScopeType;
  scopeId: string;
  warningCode: string;
  reason: string;
  details: Record<string, unknown>;
  fingerprint: string;
  confirmedById: string;
  confirmedByName: string | null;
  createdAt: string;
  resolutionId: string | null;
  resolutionComment: string | null;
  resolvedAt: string | null;
  resolvedByName: string | null;
  /** WP2: number of the correction invoice that settled a "needs re-settlement" entry. */
  settledByInvoiceNumber: string | null;
  settledByInvoice: boolean;
}

export interface ConsistencyWarningSettingsDto {
  purchasePriceDeviationPercent: number;
  updatedAt: string | null;
  updatedById: string | null;
}

function normalizeException(row: any): OperationalExceptionDto {
  return {
    id: String(row?.id ?? ''),
    scopeType: row?.scopeType,
    scopeId: String(row?.scopeId ?? ''),
    warningCode: String(row?.warningCode ?? ''),
    reason: String(row?.reason ?? ''),
    details: row?.details && typeof row.details === 'object' ? row.details : {},
    fingerprint: String(row?.fingerprint ?? ''),
    confirmedById: String(row?.confirmedById ?? ''),
    confirmedByName: row?.confirmedByName ?? null,
    createdAt: String(row?.createdAt ?? ''),
    resolutionId: row?.resolutionId ?? null,
    resolutionComment: row?.resolutionComment ?? null,
    resolvedAt: row?.resolvedAt ?? null,
    resolvedByName: row?.resolvedByName ?? null,
    settledByInvoiceNumber: row?.settledByInvoiceNumber ?? null,
    settledByInvoice: Boolean(row?.settledBySalesInvoiceId || row?.settledByPurchaseInvoiceId),
  };
}

export async function fetchOperationalExceptions(params: {
  status?: OperationalExceptionStatusFilter;
  warningCode?: string | null;
} = {}): Promise<OperationalExceptionDto[]> {
  const response = await api.get('/operational-exceptions', {
    params: {
      status: params.status || 'open',
      ...(params.warningCode ? { warningCode: params.warningCode } : {}),
    },
  });
  return Array.isArray(response.data) ? response.data.map(normalizeException) : [];
}

export async function resolveOperationalException(id: string, comment: string): Promise<OperationalExceptionDto> {
  const response = await api.post(`/operational-exceptions/${id}/resolve`, { comment });
  return normalizeException(response.data);
}

export async function fetchConsistencyWarningSettings(): Promise<ConsistencyWarningSettingsDto> {
  const response = await api.get('/operational-exceptions/settings');
  return {
    purchasePriceDeviationPercent: Number(response.data?.purchasePriceDeviationPercent ?? 15),
    updatedAt: response.data?.updatedAt ?? null,
    updatedById: response.data?.updatedById ?? null,
  };
}

export async function saveConsistencyWarningSettings(purchasePriceDeviationPercent: number): Promise<ConsistencyWarningSettingsDto> {
  const response = await api.put('/operational-exceptions/settings', { purchasePriceDeviationPercent });
  return {
    purchasePriceDeviationPercent: Number(response.data?.purchasePriceDeviationPercent ?? purchasePriceDeviationPercent),
    updatedAt: response.data?.updatedAt ?? null,
    updatedById: response.data?.updatedById ?? null,
  };
}

/** In-app route of the document an exception was confirmed on. */
export function operationalExceptionDocumentPath(exception: Pick<OperationalExceptionDto, 'scopeType' | 'scopeId'>): string | null {
  switch (exception.scopeType) {
    case 'PZ': return `/app/pz/${exception.scopeId}`;
    case 'WZ': return `/app/wz/${exception.scopeId}`;
    case 'TRANSPORT': return `/app/transports/${exception.scopeId}`;
    case 'SUPPLIER_ORDER': return `/app/supplier-orders/${exception.scopeId}`;
    case 'ORDER': return `/app/orders/${exception.scopeId}`;
    default: return null;
  }
}

// The catalogue lives with the copy that renders it, so a new code cannot
// be filterable without a Polish sentence.
export { OPERATIONAL_EXCEPTION_CODES } from './operationalExceptionText';
